import { currentLang } from './i18n';
import {
  currentUser,
  uploadFiles, setUploadFiles,
  uploadIdCounter, setUploadIdCounter,
  pendingSubtaskGoalIds, analyzingUploadIds,
  subtaskPollTimer, setSubtaskPollTimer,
  loadGoalsErrorCount, setLoadGoalsErrorCount,
  setIsGoalsLoading,
  hasLoadedGoals, setHasLoadedGoals,
  cachedAllGoals, setCachedAllGoals,
  cachedGoalsJSON, setCachedGoalsJSON,
  currentStatusFilter,
  setIsEventsLoading,
  DONE_GOALS_PAGE_SIZE,
  doneGoalsLimit, setDoneGoalsLimit,
  setDoneGoalsHasMore,
  sessionGeneration,
} from './state';
import { api } from './api';
import { showToast, showToastWithAction } from './toast';
import { sortByDeadline } from './helpers';
import { renderGoalViews, loadTodayEvents } from './goals';
import { renderSearch } from './filters';
import { loadWorkList, renderWorkDetail, requestWorkList } from './work-list';
import { switchMainTab, switchInputMode } from './navigation';
import type { FastGoalInitialOptimizationReadback, UploadEntry } from './types';
import type { PendingGoalsState } from './components/shared';
import { isFastGoalOptimizationRecoverable } from './fast-goal-optimization';

const dropZone = document.getElementById('dropZone') as HTMLElement | null;

function mergeGoalsById(...goalLists) {
  const merged = new Map();
  for (const goals of goalLists) {
    for (const goal of goals || []) {
      merged.set(goal.id, goal);
    }
  }
  return [...merged.values()];
}

dropZone?.addEventListener('dragover', e => {
  e.preventDefault();
  dropZone.classList.add('dragover');
});

dropZone?.addEventListener('dragleave', () => {
  dropZone.classList.remove('dragover');
});

dropZone?.addEventListener('drop', e => {
  e.preventDefault();
  dropZone.classList.remove('dragover');
  const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
  files.forEach(f => addFile(f));
});

export function handleFileSelect(e: Event) {
  const target = e.target as HTMLInputElement | null;
  const files = target?.files ? Array.from(target.files) : [];
  files.forEach(f => addFile(f));
  if (target) target.value = '';
}

document.addEventListener('paste', e => {
  if (!currentUser) return;
  const items = e.clipboardData?.items;
  if (!items) return;
  for (const item of items) {
    if (item.type.startsWith('image/')) {
      e.preventDefault();
      const file = item.getAsFile();
      if (file) {
        switchMainTab('create');
        switchInputMode('screenshot');
        addFile(file);
      }
      return;
    }
  }
});

export function addFile(file: File) {
  setUploadIdCounter(uploadIdCounter + 1); const id = 'uf_' + uploadIdCounter;
  const entry: UploadEntry = { id, file, dataUrl: null, status: 'pending', summary: null, goals: [], abortController: null };
  uploadFiles.push(entry);

  const reader = new FileReader();
  reader.onload = e => {
    entry.dataUrl = typeof e.target?.result === 'string' ? e.target.result : null;
    renderUploadList();
  };
  reader.readAsDataURL(file);
  renderUploadList();

  analyzeSingle(id);
}

export function removeFile(id: string) {
  setUploadFiles(uploadFiles.filter(f => f.id !== id));
  renderUploadList();
}

function buildLoadErrorMessage(message: string) {
  const wrapper = document.createElement('div');
  wrapper.className = 'msg msg-error show';
  wrapper.textContent = message;
  return wrapper;
}

function renderLoadError(message: string) {
  const todayList = document.getElementById('todayList');
  const allList = document.getElementById('allList');
  if (!todayList || !allList) return;
  todayList.replaceChildren(buildLoadErrorMessage(message));
  allList.replaceChildren(buildLoadErrorMessage(message));
}

export function renderPendingArea() {
  const pendingArea = document.getElementById('pendingGoalsArea') as (HTMLElement & { state?: PendingGoalsState }) | null;
  if (!pendingArea) return;
  const realGoals = cachedAllGoals.filter(g => pendingSubtaskGoalIds.has(g.id));
  if (analyzingUploadIds.size > 0 || realGoals.length > 0) {
    const uploadEntries = [...analyzingUploadIds]
      .map((id) => uploadFiles.find((file) => file.id === id))
      .filter((entry): entry is UploadEntry => !!entry);
    pendingArea.state = { uploadEntries, goals: realGoals };
    pendingArea.style.display = '';
  } else {
    pendingArea.state = { uploadEntries: [], goals: [] };
    pendingArea.style.display = 'none';
  }
}

export function renderUploadList() {
  const listEl = document.getElementById('uploadFileList') as (HTMLElement & { entries?: UploadEntry[] }) | null;
  if (!listEl) return;
  listEl.entries = uploadFiles.map((entry) => ({ ...entry }));
}

function showRecapturedGoalToast(goalId: string | null | undefined) {
  const message = currentLang === 'en'
    ? 'Updated an existing goal instead of creating a duplicate.'
    : '重複ゴールは作らず、既存ゴールを更新しました。';
  if (!goalId) {
    showToast(message, 'info', 6000);
    return;
  }
  const actionLabel = currentLang === 'en' ? 'Open goal' : 'ゴールを開く';
  showToastWithAction(message, actionLabel, () => {
    switchMainTab('all');
    window.location.hash = `#goal_${goalId}`;
    void loadAllGoals();
  });
}

function isInitialOptimizationPending(goal: { fast_goal_initial_optimization?: FastGoalInitialOptimizationReadback | null }) {
  if (!goal.fast_goal_initial_optimization) return null;
  return isFastGoalOptimizationRecoverable(goal.fast_goal_initial_optimization);
}

function expectedMaterializedSubtaskCount(goal: {
  grma_materialization?: { materialized_subtasks_count?: number } | null;
  metadata?: { grma_materialization_disposition?: { materialized_subtasks_count?: number } } | null;
}) {
  const clientCount = goal.grma_materialization?.materialized_subtasks_count;
  if (typeof clientCount === 'number' && clientCount > 0) return clientCount;
  const metadataCount = goal.metadata?.grma_materialization_disposition?.materialized_subtasks_count;
  return typeof metadataCount === 'number' && metadataCount > 0 ? metadataCount : 0;
}

function isMaterializedSubtaskHydrationPending(goal: {
  subtasks?: unknown[] | null;
  decisions?: unknown[] | null;
  grma_materialization?: { materialized_subtasks_count?: number } | null;
  metadata?: { grma_materialization_disposition?: { materialized_subtasks_count?: number } } | null;
}) {
  const expectedCount = expectedMaterializedSubtaskCount(goal);
  if (expectedCount <= 0) return false;
  const hydratedCount = (goal.subtasks?.length ?? 0) + (goal.decisions?.length ?? 0);
  return hydratedCount < expectedCount;
}

export async function analyzeSingle(id: string) {
  const entry = uploadFiles.find(f => f.id === id);
  if (!entry || entry.status !== 'pending') return;

  const controller = new AbortController();
  entry.abortController = controller;
  entry.status = 'analyzing';
  analyzingUploadIds.add(id);
  renderUploadList();
  renderPendingArea();

  try {
    const form = new FormData();
    form.append('screenshot', entry.file);

    const data = await api('/api/analyze', { method: 'POST', body: form, signal: controller.signal });
    entry.status = 'done';
    entry.summary = data.ai_summary || data.intake_notice?.question || data.intake_notice?.reason || '解析完了';
    entry.goals = (data.goals || []).map(g => ({ id: g.id, title: g.title }));
    if (data._recaptured) {
      showRecapturedGoalToast(data._recaptured_goal_id || data.goals?.[0]?.id || null);
    }
    for (const g of data.goals || []) {
      if ((!g.subtasks || g.subtasks.length === 0) && (!g.decisions || g.decisions.length === 0)) {
        pendingSubtaskGoalIds.add(g.id);
      }
    }
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      analyzingUploadIds.delete(id);
      removeFile(id);
      showToast(currentLang === 'en' ? 'Analysis cancelled' : '解析をキャンセルしました', 'info');
      renderPendingArea();
      return;
    }
    entry.status = 'pending';
    const message = err instanceof Error ? err.message : String(err);
    showToast('解析エラー: ' + message);
  } finally {
    analyzingUploadIds.delete(id);
    entry.abortController = null;
  }

  renderUploadList();
  loadAllGoals();
  startSubtaskPolling();
}

export function cancelAnalysis(id: string) {
  const entry = uploadFiles.find(f => f.id === id);
  if (entry && entry.abortController) {
    entry.abortController.abort();
  }
}

export function setGoalsLoadingOverlay(show: boolean) {
  document.getElementById('todayLoading')?.classList.toggle('show', show);
  document.getElementById('allLoading')?.classList.toggle('show', show);
}

// #3341: a large account's whole list took longer than the 30 seconds a
// request may, so the active goals are read this many at a time, newest first.
const ACTIVE_GOALS_PAGE_SIZE = 50;

// #3399: the later pages are read this many at a time. One after another, a
// large account's list took about 40 seconds on staging; two at a time, 22.
const ACTIVE_GOALS_PAGE_READS = 2;

function requestActiveGoalsPage(after: string | null) {
  return api(`/api/goals?exclude_done=true&page_size=${ACTIVE_GOALS_PAGE_SIZE}${after ? `&page_after=${encodeURIComponent(after)}` : ''}`);
}

/** The active goals, read a page at a time; `onPage` gets the goals read so far while more pages follow. */
export async function requestActiveGoals(onPage?: (goalsSoFar: unknown[]) => void) {
  const goals: unknown[] = [];
  const seen = new Set<string>();
  const add = (page: { goals?: Array<{ id: string }> }) => {
    for (const goal of page.goals || []) {
      if (seen.has(goal.id)) continue;
      seen.add(goal.id);
      goals.push(goal);
    }
  };
  const first = await requestActiveGoalsPage(null);
  add(first);
  const pageAfters: string[] = Array.isArray(first.page_afters)
    ? first.page_afters.filter((after: unknown): after is string => typeof after === 'string')
    : [];
  if (pageAfters.length > 0) {
    // #3399: the first page says where every later page starts, so they are
    // read side by side and shown in order as each one before them is in.
    onPage?.(goals);
    const reads: Promise<{ goals?: Array<{ id: string }> }>[] = [];
    let next = 0;
    const startNext = () => {
      if (next >= pageAfters.length) return;
      const read = requestActiveGoalsPage(pageAfters[next++]!);
      // A page that fails is thrown when its turn comes, not before.
      read.catch(() => {});
      reads.push(read);
    };
    for (let i = 0; i < ACTIVE_GOALS_PAGE_READS; i += 1) startNext();
    for (let index = 0; index < pageAfters.length; index += 1) {
      const page = await reads[index]!;
      startNext();
      add(page);
      if (index < pageAfters.length - 1) onPage?.(goals);
    }
    return { goals };
  }
  let after: string | null = typeof first.next_page_after === 'string' ? first.next_page_after : null;
  while (after) {
    onPage?.(goals);
    const page = await requestActiveGoalsPage(after);
    add(page);
    after = typeof page.next_page_after === 'string' ? page.next_page_after : null;
  }
  return { goals };
}

// #2739: the done list shows the next page of older completed Goals.
export function loadMoreDoneGoals() {
  setDoneGoalsLimit(doneGoalsLimit + DONE_GOALS_PAGE_SIZE);
  return loadAllGoals({ silent: true });
}

type LoadAllGoalsOptions = {
  silent?: boolean;
  /** A refresh that need not show a change made just now: skipped while a read is already running. */
  poll?: boolean;
  /** A /api/work-list request already started (enterApp starts it alongside /api/profile). */
  workListRequest?: ReturnType<typeof requestWorkList>;
};

// A read of a large account can take longer than the 30-second refresh, so
// reads never overlap on the server: a refresh while one runs is dropped, and
// any other caller (after a change) waits for one more read that starts when
// the running one ends, shared by everyone who asks meanwhile.
let runningGoalsRead: Promise<void> | null = null;
let nextGoalsRead: Promise<void> | null = null;

export function loadAllGoals(options: LoadAllGoalsOptions = {}): Promise<void> {
  if (!runningGoalsRead) {
    const read = readAllGoals(options).finally(() => {
      if (runningGoalsRead === read) runningGoalsRead = null;
    });
    runningGoalsRead = read;
    return read;
  }
  if (options.poll) return runningGoalsRead;
  if (!nextGoalsRead) {
    const generation = sessionGeneration;
    nextGoalsRead = runningGoalsRead.catch(() => {}).then(() => {
      nextGoalsRead = null;
      if (generation !== sessionGeneration) return;
      return loadAllGoals({ silent: options.silent });
    });
  }
  return nextGoalsRead;
}

async function readAllGoals({ silent = false, workListRequest: startedWorkListRequest }: LoadAllGoalsOptions) {
  if (!silent) {
    setIsGoalsLoading(true);
    setGoalsLoadingOverlay(true);
  }

  // #2749: the first tab's list is read alongside the goals. It is drawn as
  // soon as it arrives, so the first tab does not wait for the goals.
  const workListRequest = startedWorkListRequest ?? requestWorkList();
  workListRequest.catch(() => {});
  const generation = sessionGeneration;
  // The day's events are read as soon as the list has answered, not after all
  // the goals: arriving last, their band pushed the whole list down.
  let eventsRead: Promise<void> | null = null;
  if (!silent) {
    workListRequest.then(() => {
      if (generation === sessionGeneration && !hasLoadedGoals) void loadWorkList(workListRequest);
    }, () => {});
    if (!hasLoadedGoals) {
      eventsRead = workListRequest.catch(() => {}).then(() => {
        if (generation !== sessionGeneration) return;
        setIsEventsLoading(true);
        return loadTodayEvents().catch(() => {}).finally(() => setIsEventsLoading(false));
      });
    }
  }
  try {
    // #3341: both reads compose every active Goal on the server. For a large
    // account the two at once made the server drop them (502), so the goals
    // are asked for only once the list has answered.
    // The first read draws the goals of each page as it arrives; a later one
    // keeps the goals already shown until it has them all.
    const firstRead = !hasLoadedGoals;
    const showPage = (goalsSoFar: unknown[]) => {
      if (silent || !firstRead || generation !== sessionGeneration) return;
      setCachedAllGoals(sortByDeadline(goalsSoFar));
      setHasLoadedGoals(true);
      setGoalsLoadingOverlay(false);
      renderGoalViews();
      renderSearch();
    };
    const readActive = () => requestActiveGoals(showPage);
    const activeData = await workListRequest.then(readActive, readActive);
    const doneData = currentStatusFilter === 'done'
      ? await api(`/api/goals?status=done&limit=${doneGoalsLimit}`)
      : { goals: [] };
    // #3138: a reply to a request made before a sign-out or account switch is dropped.
    if (generation !== sessionGeneration) return;
    setLoadGoalsErrorCount(0);
    setDoneGoalsHasMore(doneData.has_more_done === true);

    const goals = mergeGoalsById(activeData.goals || [], doneData.goals || []);
    const newJSON = JSON.stringify(goals);
    if (silent && newJSON === cachedGoalsJSON) {
      await loadWorkList(workListRequest);
      return;
    }
    setCachedGoalsJSON(newJSON);

    setCachedAllGoals(sortByDeadline(goals));
    setHasLoadedGoals(true);

    // The durable row, rather than process memory, restores polling after a
    // reload or deployment. A stale generation remains recoverable, but uses a
    // non-spinning warning in the card while the scheduler reclaims it.
    for (const goal of cachedAllGoals) {
      if (isInitialOptimizationPending(goal) === true) {
        pendingSubtaskGoalIds.add(goal.id);
      }
    }
    if (pendingSubtaskGoalIds.size > 0) startSubtaskPolling();

    if (pendingSubtaskGoalIds.size > 0) {
      for (const id of [...pendingSubtaskGoalIds]) {
        const g = cachedAllGoals.find(g => g.id === id);
        const optimizationPending = g ? isInitialOptimizationPending(g) : null;
        if (g && !isMaterializedSubtaskHydrationPending(g) && (
          optimizationPending === false ||
          (optimizationPending === null && (
            (g.subtasks && g.subtasks.length > 0) ||
            (g.decisions && g.decisions.length > 0) ||
            g.subtask_generation_status === 'failed' ||
            g.subtask_generation_status === 'success'
          ))
        )) {
          pendingSubtaskGoalIds.delete(id);
        }
      }
      if (pendingSubtaskGoalIds.size === 0) stopSubtaskPolling();
    }

    const pageScrollY = window.scrollY;
    const aiScrollMap = {};
    document.querySelectorAll('.ai-result-content[data-subtask-id]').forEach(el => {
      if (el.scrollTop > 0) aiScrollMap[(el as HTMLElement).dataset.subtaskId] = el.scrollTop;
    });

    await loadWorkList(workListRequest);
    renderGoalViews();
    renderSearch();
    renderPendingArea();

    document.querySelectorAll('.ai-result-content[data-subtask-id]').forEach(el => {
      (el as HTMLElement).style.height = 'auto';
      (el as HTMLElement).style.height = el.scrollHeight + 'px';
    });

    if (silent && (pageScrollY > 0 || Object.keys(aiScrollMap).length > 0)) {
      requestAnimationFrame(() => {
        window.scrollTo(0, pageScrollY);
        Object.entries(aiScrollMap).forEach(([id, top]) => {
          const el = document.querySelector(`.ai-result-content[data-subtask-id="${id}"]`);
          if (el) el.scrollTop = top as number;
        });
      });
    }
    if (!eventsRead) {
      setIsEventsLoading(true);
      loadTodayEvents().catch(() => {}).finally(() => setIsEventsLoading(false));
    }
  } catch (err) {
    // A read that failed because the sign-in ended is not a connection error;
    // the login screen already answers it.
    if (generation !== sessionGeneration) return;
    setLoadGoalsErrorCount(loadGoalsErrorCount + 1);
    if (loadGoalsErrorCount >= 3) {
      stopSubtaskPolling();
      pendingSubtaskGoalIds.clear();
      renderLoadError('接続エラーが続いています。ページを再読み込みしてください。');
      return;
    }
    renderLoadError('接続エラーが発生しました。しばらくしてから再度お試しください。');
  } finally {
    setIsGoalsLoading(false);
    setGoalsLoadingOverlay(false);
    // A detail waiting for its goal either has it now or closes.
    renderWorkDetail();
  }
}

export function startSubtaskPolling() {
  if (subtaskPollTimer) return;
  setSubtaskPollTimer(setInterval(() => {
    if (pendingSubtaskGoalIds.size === 0) { stopSubtaskPolling(); return; }
    loadAllGoals({ silent: true, poll: true });
  }, 3000));
}

export function stopSubtaskPolling() {
  if (subtaskPollTimer) { clearInterval(subtaskPollTimer); setSubtaskPollTimer(null); }
}

export async function retrySubtaskGeneration(goalId: string, btn: HTMLButtonElement) {
  btn.disabled = true;
  try {
    await api(`/api/goals/${goalId}/regenerate-subtasks`, { method: 'POST' });
    pendingSubtaskGoalIds.add(goalId);
    showToast(currentLang === 'en' ? 'Retrying task generation...' : 'タスク生成を再試行中...', 'info');
    startSubtaskPolling();
    loadAllGoals({ silent: true });
  } catch (err) {
    showToast(currentLang === 'en' ? 'Retry failed' : '再試行に失敗しました');
    btn.disabled = false;
  }
}
